import React from 'react'
import _ from 'underscore'
import moment from 'moment-timezone'

import styled, { css } from 'styled-components'

import { ILegend, LegendItem, legendDataForTimeAtCursor } from 'types/RuntimeHistory'
import { color } from 'styles/colors'

import AlertsLegendGroup from 'pages/Device/ThermostatRthPanel/Legend/AlertsLegendGroup'
import LegendGroup from 'pages/Device/ThermostatRthPanel/Legend/LegendGroup'
import IndoorLegendGroup from 'pages/Device/ThermostatRthPanel/Legend/IndoorLegendGroup'
import LegendConfig from 'pages/Device/ThermostatRthPanel/Legend/legendItemsConfig'

import { screenSize } from 'styles/shared'
import { Alert, DeviceConnectionState } from 'types/Alert'

type Props = {
  timeAtCursor: number | null
  legendData: Required<ILegend>
  timeZone: string
  isChartHovered: boolean
  isServerlessRth?: boolean
  isControlHub: boolean
  legendDataForTimeAtCursor: legendDataForTimeAtCursor | null
  alertsInVisibleRange: Array<Alert>
  deviceConnectionStatusAlerts: Array<DeviceConnectionState>
  selectedLegends: {
    series: Array<string>,
    zoneSeries: Array<{ name: string, series: Array<string> }>,
    alerts: Array<string>
  }
  isLegendItemHasData: (l: LegendItem, d: ILegend) => boolean
  onClickAlertsLegendItem: (i) => void
  onClickAlertsLegendAllOrNone: (i, n) => void
  onIndoorLegendItemClickCallback: (i, n) => void
  onLegendItemClickCallback: (i, n?) => void
  onIndoorLegendItemSelectAllClickCallback: (i, n) => void
  onLegendItemSelectAllClickCallback: (i, n) => void
  onIndoorZoneClickCallback: (series, zoneName) => void
}

const Legend = ({
  timeAtCursor,
  legendData,
  isChartHovered,
  isControlHub,
  isServerlessRth = false,
  timeZone,
  legendDataForTimeAtCursor,
  selectedLegends,
  alertsInVisibleRange,
  deviceConnectionStatusAlerts,
  isLegendItemHasData,
  onClickAlertsLegendItem,
  onClickAlertsLegendAllOrNone,
  onIndoorZoneClickCallback,
  onIndoorLegendItemSelectAllClickCallback,
  onLegendItemSelectAllClickCallback,
  onLegendItemClickCallback,
  onIndoorLegendItemClickCallback
}: Props) => {
  const indoorUnitLegendItems: Array<LegendItem> = isControlHub ? _.union(LegendConfig.INDOOR_UNIT_LEGEND_ITEMS, LegendConfig.CONTROL_HUB_INDOOR_UNIT_LEGEND_ITEMS) : LegendConfig.INDOOR_UNIT_LEGEND_ITEMS
  const indoorLegendItems = legendData.zones.length > 1 ? LegendConfig.INDOOR_ZONE_LEGEND_ITEMS : LegendConfig.INDOOR_LEGEND_ITEMS

  _.each(indoorUnitLegendItems, (legendItem) => { legendItem.emptyDataSet = !isLegendItemHasData(legendItem, legendData) })
  _.each(LegendConfig.OUTDOOR_LEGEND_ITEMS, (legendItem) => { legendItem.emptyDataSet = !isLegendItemHasData(legendItem, legendData) })
  _.each(LegendConfig.OUTDOOR_UNIT_LEGEND_ITEMS, (legendItem) => { legendItem.emptyDataSet = !isLegendItemHasData(legendItem, legendData) })

  const shouldDisplayLegendGroup = (legendGroup) => _.any(legendGroup, (item) => !item.emptyDataSet)
  const displayIndoorUnitGroup = shouldDisplayLegendGroup(indoorUnitLegendItems)
  const displayOutdoorGroup = shouldDisplayLegendGroup(LegendConfig.OUTDOOR_LEGEND_ITEMS)
  const displayOutdoorUnitGroup = shouldDisplayLegendGroup(LegendConfig.OUTDOOR_UNIT_LEGEND_ITEMS)

  const selectedOutdoorUnitLegends = _.filter(selectedLegends.series, (seriesName) => _.any(LegendConfig.OUTDOOR_UNIT_LEGEND_ITEMS, item => item.seriesName === seriesName))
  const selectedOutdoorLegends = _.filter(selectedLegends.series, (seriesName) => _.any(LegendConfig.OUTDOOR_LEGEND_ITEMS, item => item.seriesName === seriesName))
  const selectedIndoorUnitLegends = _.filter(selectedLegends.series, (seriesName) => _.any(indoorUnitLegendItems, item => item.seriesName === seriesName))

  return (
    <S.Legend {...{ isChartHovered }}>
      {isChartHovered && <S.Time>{moment(timeAtCursor).tz(timeZone || moment.tz.guess()).format('h:mm A')}</S.Time>}

      <S.LegendGroupContainer>
        {indoorLegendItems && legendData.zones && (
          <IndoorLegendGroup
            {...{ legendDataForTimeAtCursor }}
            legendItems={indoorLegendItems}
            zones={legendData.zones}
            onIndoorLegendItemClickCallback={onIndoorLegendItemClickCallback}
            onIndoorLegendSelectAllCallback={onIndoorLegendItemSelectAllClickCallback}
            onIndoorZoneClickCallback={onIndoorZoneClickCallback}
            selectedLegends={selectedLegends.zoneSeries}
          />
        )}

        {displayIndoorUnitGroup && (
          <LegendGroup {...{ legendDataForTimeAtCursor }}
            onLegendItemClickCallback={onLegendItemClickCallback}
            onLegendItemSelectAllClickCallback={onLegendItemSelectAllClickCallback}
            label="Indoor Unit"
            isControlHub={isControlHub}
            legendItems={indoorUnitLegendItems}
            selectedLegends={selectedIndoorUnitLegends}
          />
        )}

        <S.OutdoorLegendGroupContainer>
          {displayOutdoorGroup && (
            <LegendGroup {...{ legendDataForTimeAtCursor }}
              onLegendItemClickCallback={onLegendItemClickCallback}
              onLegendItemSelectAllClickCallback={onLegendItemSelectAllClickCallback}
              label="Outdoor"
              legendItems={LegendConfig.OUTDOOR_LEGEND_ITEMS}
              selectedLegends={selectedOutdoorLegends}
              showCompressorSpeedInOutdoor={!displayOutdoorUnitGroup}
            />
          )}

          {displayOutdoorUnitGroup && (
            <LegendGroup {...{ legendDataForTimeAtCursor }}
              onLegendItemClickCallback={onLegendItemClickCallback}
              onLegendItemSelectAllClickCallback={onLegendItemSelectAllClickCallback}
              label="Outdoor Unit"
              legendItems={LegendConfig.OUTDOOR_UNIT_LEGEND_ITEMS}
              selectedLegends={selectedOutdoorUnitLegends}
            />
          )}
        </S.OutdoorLegendGroupContainer>

        <AlertsLegendGroup
          alerts={alertsInVisibleRange}
          deviceConnectionStatusAlerts={deviceConnectionStatusAlerts}
          selectedLegends={selectedLegends.alerts || []}
          isServerlessRth={isServerlessRth}
          onClickLegendItem={onClickAlertsLegendItem}
          onClickAllOrNone={onClickAlertsLegendAllOrNone}
        />
      </S.LegendGroupContainer>
    </S.Legend>
  )
}

export default Legend

const S = {
  Legend: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isChartHovered'].includes(prop)
  })<{ isChartHovered: boolean }>`
    padding-top: 25px;
    padding-bottom: 20px;
    padding-left: 10px;
    margin: -30px 34px 0 34px;
    overflow: hidden;

    ${({ isChartHovered }) => {
    if (isChartHovered) {
      return css`
          position: relative;
          margin-top: -26px;
          border-radius: 5px;
          box-shadow:
            4px 4px 4px -4px ${color('rthLegendBorderHover')},
            4px 4px 4px -4px ${color('rthLegendBorderHover')},
            -4px 4px 4px -4px ${color('rthLegendBorderHover')},
            -4px -4px 4px -4px ${color('rthLegendBorderHover')},
            4px -4px 4px -4px ${color('rthLegendBorderHover')};
        `
    }
  }}
  `,
  LegendGroupContainer: styled.div`
    display: flex;
    flex-flow: row wrap;
  `,
  OutdoorLegendGroupContainer: styled.div`
    display: flex;
    flex-direction: column;

    @media ${screenSize.medium} {
      flex-direction: row;
    }
  `,
  Time: styled.div`
    width: 100%;
    margin-top: -30px;
    margin-bottom: -11px;
    font-weight: bold;
    font-size: 27px;
    text-align: center;
    color: ${color('rthLegendTime')};
  `
}
